import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, CalendarClock, FileDown, Inbox, RefreshCw, Lock, Download, Send } from "lucide-react";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "klc_admin_token";

function fmt(dtIso) {
  if (!dtIso) return "—";
  try {
    return new Date(dtIso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return dtIso;
  }
}
function fmtSlot(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A14] flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0B132B]/60 p-8">
        <div className="flex items-center gap-3 mb-8 text-[#F2EFE9]">
          <LogoMark size={30} />
          <div>
            <p className="font-900 tracking-tightest leading-none">KLC Admin</p>
            <p className="text-xs text-[#94A3B8] mt-1">Private dashboard</p>
          </div>
        </div>
        <label className="text-xs font-500 tracking-widest uppercase text-[#94A3B8]">Email</label>
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email"
          className="mt-2 mb-4 w-full bg-[#050A14] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors" />
        <label className="text-xs font-500 tracking-widest uppercase text-[#94A3B8]">Password</label>
        <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password"
          className="mt-2 mb-6 w-full bg-[#050A14] border border-white/10 rounded-xl px-4 py-3 text-[#F2EFE9] placeholder:text-[#94A3B8] focus:border-[#D95A41] outline-none transition-colors" />
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="w-full py-3.5 rounded-full bg-[#D95A41] text-[#F2EFE9] font-700 hover:bg-[#E6735A] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          <Lock size={16} /> {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

const TABS = [
  { key: "contacts", label: "Enquiries", icon: Mail },
  { key: "bookings", label: "Bookings", icon: CalendarClock },
  { key: "leads", label: "Profile Leads", icon: FileDown },
];

const CSV_COLS = {
  contacts: [["Name", "name"], ["Email", "email"], ["Company", "company"], ["Message", "message"], ["Received", "created_at"]],
  bookings: [["Name", "name"], ["Email", "email"], ["Slot", "slot"], ["Notes", "notes"], ["Booked", "created_at"]],
  leads: [["Name", "name"], ["Email", "email"], ["Source", "source"], ["Date", "created_at"]],
};

function exportCsv(tab, rows) {
  const cols = CSV_COLS[tab];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = cols.map((c) => esc(c[0])).join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c[1]])).join(",")).join("\n");
  const blob = new Blob([`\ufeff${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `klc-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [tab, setTab] = useState("contacts");
  const [stats, setStats] = useState({ contacts: 0, bookings: 0, leads: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const prevStats = useRef(null);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  const notifyNew = useCallback((next) => {
    const prev = prevStats.current;
    if (prev) {
      const labels = { contacts: "enquiry", bookings: "booking", leads: "profile download" };
      Object.keys(labels).forEach((k) => {
        const delta = (next[k] || 0) - (prev[k] || 0);
        if (delta > 0) {
          const msg = `${delta} new ${labels[k]}${delta > 1 ? "s" : ""}`;
          toast.success(msg);
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification("Kamboj Law Chambers", { body: msg }); } catch { /* noop */ }
          }
        }
      });
    }
    prevStats.current = next;
  }, []);

  const check = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setAuthed(false); return; }
    try {
      await axios.get(`${API}/auth/me`, authHeader());
      setAuthed(true);
    } catch {
      logout();
    }
  }, [logout]);

  useEffect(() => { check(); }, [check]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, authHeader());
      setStats(data);
      notifyNew(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout, notifyNew]);

  const sendDigest = useCallback(async () => {
    setDigestSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/digest`, {}, authHeader());
      toast.success(`Digest emailed — ${data.contacts} enquiries, ${data.bookings} bookings, ${data.leads} leads.`);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error(errMsg(e));
    } finally {
      setDigestSending(false);
    }
  }, [logout]);

  const loadRows = useCallback(async (key) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/${key}`, authHeader());
      setRows(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (authed) { loadStats(); loadRows(tab); }
  }, [authed, tab, loadStats, loadRows]);

  // Poll for new activity every 30s + request desktop notification permission
  useEffect(() => {
    if (!authed) return undefined;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const id = setInterval(() => { loadStats(); }, 30000);
    return () => clearInterval(id);
  }, [authed, loadStats]);

  if (authed === null) {
    return <div className="min-h-screen bg-[#050A14] flex items-center justify-center text-[#94A3B8]">Loading…</div>;
  }
  if (authed === false) {
    return <Login onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#050A14] text-[#F2EFE9]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-[#050A14]/80 backdrop-blur-xl z-10">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={26} />
            <span className="font-900 tracking-tightest">KLC Admin</span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button onClick={sendDigest} disabled={digestSending} data-testid="admin-digest-btn"
              className="inline-flex items-center gap-2 text-sm font-500 px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 transition-colors disabled:opacity-60">
              <Send size={15} /> <span className="hidden sm:inline">{digestSending ? "Sending…" : "Email digest"}</span>
            </button>
            <button onClick={() => { loadStats(); loadRows(tab); }} data-testid="admin-refresh"
              className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Refresh"><RefreshCw size={18} /></button>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm font-500 px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 transition-colors">
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`stat-${t.key}`}
                className={`text-left rounded-2xl border p-6 transition-colors ${tab === t.key ? "border-[#D95A41] bg-[#0B132B]" : "border-white/10 bg-[#0B132B]/40 hover:border-white/25"}`}>
                <div className="flex items-center justify-between">
                  <Icon size={20} className="text-[#D95A41]" />
                  <span className="text-3xl font-900">{stats[t.key] ?? 0}</span>
                </div>
                <p className="mt-3 text-sm text-[#94A3B8]">{t.label}</p>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Inbox size={16} className="text-[#D95A41]" />
              <h2 className="font-700">{TABS.find((t) => t.key === tab)?.label}</h2>
              <span className="text-sm text-[#94A3B8]">({rows.length})</span>
            </div>
            <button
              onClick={() => exportCsv(tab, rows)}
              disabled={rows.length === 0}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 text-sm font-500 px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 transition-colors disabled:opacity-40"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>

          {loading ? (
            <div className="p-10 text-center text-[#94A3B8]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-[#94A3B8]" data-testid="admin-empty">Nothing here yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="text-left text-[#94A3B8] border-b border-white/10">
                    {tab === "contacts" && <><th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Company</th><th className="px-6 py-3">Message</th><th className="px-6 py-3">Received</th></>}
                    {tab === "bookings" && <><th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Slot</th><th className="px-6 py-3">Notes</th><th className="px-6 py-3">Booked</th></>}
                    {tab === "leads" && <><th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Source</th><th className="px-6 py-3">Date</th></>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03]">
                      {tab === "contacts" && <>
                        <td className="px-6 py-3 font-500">{r.name}</td>
                        <td className="px-6 py-3"><a href={`mailto:${r.email}`} className="text-[#D95A41] hover:underline">{r.email}</a></td>
                        <td className="px-6 py-3 text-[#94A3B8]">{r.company || "—"}</td>
                        <td className="px-6 py-3 text-[#94A3B8] max-w-md">{r.message}</td>
                        <td className="px-6 py-3 text-[#94A3B8] whitespace-nowrap">{fmt(r.created_at)}</td>
                      </>}
                      {tab === "bookings" && <>
                        <td className="px-6 py-3 font-500">{r.name}</td>
                        <td className="px-6 py-3"><a href={`mailto:${r.email}`} className="text-[#D95A41] hover:underline">{r.email}</a></td>
                        <td className="px-6 py-3 whitespace-nowrap">{fmtSlot(r.slot)}</td>
                        <td className="px-6 py-3 text-[#94A3B8] max-w-xs">{r.notes || "—"}</td>
                        <td className="px-6 py-3 text-[#94A3B8] whitespace-nowrap">{fmt(r.created_at)}</td>
                      </>}
                      {tab === "leads" && <>
                        <td className="px-6 py-3 font-500">{r.name || "—"}</td>
                        <td className="px-6 py-3"><a href={`mailto:${r.email}`} className="text-[#D95A41] hover:underline">{r.email}</a></td>
                        <td className="px-6 py-3 text-[#94A3B8]">{r.source || "—"}</td>
                        <td className="px-6 py-3 text-[#94A3B8] whitespace-nowrap">{fmt(r.created_at)}</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
